import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../Common/Layout/AdminHeader';
import { rexTechTheme } from '../Common/Theme';
import {
  Users,
  UserCheck,
  ShoppingBag,
  Package,
  TrendingUp,
  Calendar,
  Bell,
  Search,
  Filter,
  Download,
  MoreVertical,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  RefreshCw,
  UserPlus,
  CheckCircle,
  AlertCircle,
  Building,
  BarChart3,
  Activity
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

// Import your API configuration
import config from '../Common/Configurations/APIConfig';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({
    clients: 0,
    collectors: 0,
    buyers: 0,
    requests: 0
  });
  
  const [trendData, setTrendData] = useState({
    clientTrend: '0%',
    clientDirection: 'up',
    collectorTrend: '0%',
    collectorDirection: 'up',
    buyerTrend: '0%',
    buyerDirection: 'up',
    requestTrend: '0%',
    requestDirection: 'up'
  });

  // Chart data states
  const [clientGrowthData, setClientGrowthData] = useState([]);
  const [userActivityData, setUserActivityData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  
  // Filter states
  const [periodFilter, setPeriodFilter] = useState('week');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Fetch dashboard data
  const fetchDashboardData = async (period = periodFilter) => {
    try {
      setIsLoading(true);
      setIsRefreshing(true);
      
      // Fetch summary data
      const summaryResponse = await axios.get(
        `${config.APIACTIVEURL}dashboard/summary?period=${period}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (summaryResponse.data.success) {
        const data = summaryResponse.data.data;
        
        // Update stats
        setStats({
          clients: data.statistics?.clients || 0,
          collectors: data.statistics?.collectors || 0,
          buyers: data.statistics?.buyers || 0,
          requests: data.statistics?.requests || 0
        });

        // Update trend data
        setTrendData({
          clientTrend: data.statistics?.clientTrendValue || '0%',
          clientDirection: data.statistics?.clientTrendDirection || 'up',
          collectorTrend: data.statistics?.collectorTrendValue || '0%',
          collectorDirection: data.statistics?.collectorTrendDirection || 'up',
          buyerTrend: data.statistics?.buyerTrendValue || '0%',
          buyerDirection: data.statistics?.buyerTrendDirection || 'up',
          requestTrend: data.statistics?.requestTrendValue || '0%',
          requestDirection: data.statistics?.requestTrendDirection || 'up'
        });

        // Update recent activities
        setRecentActivities(data.recentActivities?.map(activity => ({
          id: activity.userId,
          user: activity.userName,
          action: activity.action,
          time: activity.timeAgo,
          type: activity.type?.toLowerCase() || 'user',
          branch: activity.branchName,
          timestamp: activity.timestamp
        })) || []);
      }

      // Fetch charts data
      const chartsResponse = await axios.get(
        `${config.APIACTIVEURL}dashboard/charts?period=${period}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (chartsResponse.data.success) {
        const charts = chartsResponse.data.data;
        
        // Update client growth chart
        if (charts.clientGrowth?.data) {
          setClientGrowthData(charts.clientGrowth.data.map(item => ({
            period: item.period,
            clients: item.clients
          })));
        }

        // Update user activity chart
        if (charts.userActivity?.data) {
          setUserActivityData(charts.userActivity.data.map(item => ({
            day: item.day,
            active: item.activeUsers,
            new: item.newUsers
          })));
        }
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Handle errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else if (error.response?.status === 403) {
        alert('Access denied. You need admin privileges.');
      } else {
        // Show error message
        alert('Failed to load dashboard data. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch role statistics
  const fetchRoleStatistics = async () => {
    try {
      const response = await axios.get(
        `${config.APIACTIVEURL}dashboard/role-stats`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data.success ? response.data.data : null;
    } catch (error) {
      console.error('Error fetching role statistics:', error);
      return null;
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Handle period change
  const handlePeriodChange = (period) => {
    setPeriodFilter(period);
    fetchDashboardData(period);
  };

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date range submit
  const handleDateRangeSubmit = () => {
    // You can implement custom date range filtering here
    alert('Custom date range filtering coming soon!');
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchDashboardData();
  };

 
  // Stats Card Component
  const StatsCard = ({ title, value, icon: Icon, color, trendDirection, trendValue, onClick, loading }) => {
    const trendColor = trendDirection === 'up' ? rexTechTheme.colors.success : rexTechTheme.colors.error;
    const TrendIcon = trendDirection === 'up' ? ArrowUpRight : ArrowDownRight;

    return (
      <div
        onClick={onClick}
        style={{
          backgroundColor: rexTechTheme.colors.neutral.white,
          borderRadius: rexTechTheme.borderRadius.large,
          padding: rexTechTheme.spacing.xl,
          boxShadow: rexTechTheme.shadows.light,
          border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '140px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ':hover': onClick ? {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
            borderColor: color
          } : {}
        }}
      >
        {/* Background accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '60px',
          height: '60px',
          backgroundColor: `${color}10`,
          borderRadius: '0 0 0 100%',
          transition: 'all 0.3s ease'
        }} />
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <p style={{
              color: rexTechTheme.colors.neutral.darkGray,
              fontSize: rexTechTheme.typography.fontSize.regular,
              marginBottom: rexTechTheme.spacing.xs,
              fontWeight: rexTechTheme.typography.fontWeight.medium
            }}>
              {title}
            </p>
            <h3 style={{
              color: rexTechTheme.colors.primary.dark,
              fontSize: rexTechTheme.typography.fontSize.xxxlarge,
              fontWeight: rexTechTheme.typography.fontWeight.bold,
              margin: 0,
              lineHeight: 1,
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {loading ? (
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: `2px solid ${rexTechTheme.colors.primary.light}`,
                  borderTopColor: rexTechTheme.colors.primary.main,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                value.toLocaleString()
              )}
            </h3>
            
            {trendValue && !loading && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.xs,
                marginTop: rexTechTheme.spacing.sm
              }}>
                <TrendIcon size={16} color={trendColor} />
                <span style={{
                  color: trendColor,
                  fontSize: rexTechTheme.typography.fontSize.small,
                  fontWeight: rexTechTheme.typography.fontWeight.semiBold
                }}>
                  {trendValue}
                </span>
                <span style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  fontSize: rexTechTheme.typography.fontSize.xs,
                  marginLeft: rexTechTheme.spacing.xs
                }}>
                  from last month
                </span>
              </div>
            )}
          </div>
          
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
            flexShrink: 0
          }}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  // Activity Item Component
  const ActivityItem = ({ activity }) => {
    const getActivityColor = (type) => {
      switch (type) {
        case 'client': return rexTechTheme.colors.primary.main;
        case 'collector': return rexTechTheme.colors.success;
        case 'buyer': return rexTechTheme.colors.info;
        case 'request': return rexTechTheme.colors.warning;
        case 'admin': return rexTechTheme.colors.error;
        default: return rexTechTheme.colors.neutral.darkGray;
      }
    };

    const getActivityIcon = (type) => {
      switch (type) {
        case 'client': return <Users size={20} />;
        case 'collector': return <UserCheck size={20} />;
        case 'buyer': return <ShoppingBag size={20} />;
        case 'request': return <Package size={20} />;
        case 'admin': return <UserCheck size={20} />;
        default: return <Users size={20} />;
      }
    };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: rexTechTheme.spacing.md,
        padding: rexTechTheme.spacing.md,
        backgroundColor: rexTechTheme.colors.neutral.white,
        borderRadius: rexTechTheme.borderRadius.medium,
        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
        marginBottom: rexTechTheme.spacing.sm,
        transition: 'all 0.2s ease',
        ':hover': {
          backgroundColor: rexTechTheme.colors.primary.lightest,
          transform: 'translateX(4px)'
        }
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: `${getActivityColor(activity.type)}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: getActivityColor(activity.type),
          flexShrink: 0
        }}>
          {getActivityIcon(activity.type)}
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: rexTechTheme.spacing.sm,
            marginBottom: rexTechTheme.spacing.xs
          }}>
            <p style={{
              margin: 0,
              color: rexTechTheme.colors.primary.dark,
              fontWeight: rexTechTheme.typography.fontWeight.medium,
              fontSize: rexTechTheme.typography.fontSize.regular
            }}>
              {activity.user}
            </p>
            {activity.branch && (
              <span style={{
                padding: `2px ${rexTechTheme.spacing.xs}`,
                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                color: rexTechTheme.colors.neutral.darkGray,
                borderRadius: rexTechTheme.borderRadius.small,
                fontSize: rexTechTheme.typography.fontSize.xs,
                fontWeight: rexTechTheme.typography.fontWeight.medium
              }}>
                {activity.branch}
              </span>
            )}
          </div>
          <p style={{
            margin: 0,
            color: rexTechTheme.colors.neutral.darkGray,
            fontSize: rexTechTheme.typography.fontSize.small,
            lineHeight: 1.4
          }}>
            {activity.action}
          </p>
        </div>
        
        <div style={{
          color: rexTechTheme.colors.neutral.darkGray,
          fontSize: rexTechTheme.typography.fontSize.xs,
          textAlign: 'right',
          minWidth: '80px',
          flexShrink: 0
        }}>
          {activity.time}
        </div>
      </div>
    );
  };

  // Custom Tooltip for Charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: rexTechTheme.colors.neutral.white,
          border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
          padding: rexTechTheme.spacing.md,
          borderRadius: rexTechTheme.borderRadius.medium,
          boxShadow: rexTechTheme.shadows.medium
        }}>
          <p style={{
            margin: 0,
            color: rexTechTheme.colors.primary.dark,
            fontWeight: 'bold',
            marginBottom: rexTechTheme.spacing.xs
          }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{
              margin: 0,
              color: entry.color,
              fontSize: rexTechTheme.typography.fontSize.small,
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.xs
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                backgroundColor: entry.color,
                borderRadius: '2px'
              }} />
              {entry.name}: <strong>{entry.value}</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div style={{
      minHeight: '100vh',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      <AdminHeader />
      
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: rexTechTheme.spacing.xl
      }}>
        {/* Header skeleton */}
        <div style={{ marginBottom: rexTechTheme.spacing.xl }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div>
              <div style={{
                width: '200px',
                height: '32px',
                backgroundColor: rexTechTheme.colors.neutral.gray,
                borderRadius: rexTechTheme.borderRadius.medium,
                marginBottom: rexTechTheme.spacing.xs
              }} />
              <div style={{
                width: '300px',
                height: '20px',
                backgroundColor: rexTechTheme.colors.neutral.gray,
                borderRadius: rexTechTheme.borderRadius.medium
              }} />
            </div>
            <div style={{
              width: '120px',
              height: '40px',
              backgroundColor: rexTechTheme.colors.neutral.gray,
              borderRadius: rexTechTheme.borderRadius.medium
            }} />
          </div>
        </div>

        {/* Stats grid skeleton */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: rexTechTheme.spacing.lg,
          marginBottom: rexTechTheme.spacing.xl
        }}>
          {[1, 2, 3, 4].map((item) => (
            <div key={item} style={{
              backgroundColor: rexTechTheme.colors.neutral.white,
              borderRadius: rexTechTheme.borderRadius.large,
              padding: rexTechTheme.spacing.xl,
              height: '140px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div style={{
                width: '80px',
                height: '20px',
                backgroundColor: rexTechTheme.colors.neutral.gray,
                borderRadius: rexTechTheme.borderRadius.medium
              }} />
              <div style={{
                width: '60px',
                height: '32px',
                backgroundColor: rexTechTheme.colors.neutral.gray,
                borderRadius: rexTechTheme.borderRadius.medium
              }} />
            </div>
          ))}
        </div>

        {/* Charts skeleton */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: rexTechTheme.spacing.xl,
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.xl,
            height: '400px'
          }} />
          <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.xl,
            height: '400px'
          }} />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      <AdminHeader />
      
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: rexTechTheme.spacing.xl
      }}>
        {/* Header */}
        <div style={{
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div>
              <h1 style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                marginBottom: rexTechTheme.spacing.xs,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <BarChart3 size={32} />
                Dashboard Overview
              </h1>
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.regular,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <Calendar size={16} />
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.md
            }}>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                style={{
                  padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.lg}`,
                  backgroundColor: rexTechTheme.colors.neutral.white,
                  border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                  borderRadius: rexTechTheme.borderRadius.medium,
                  cursor: 'pointer',
                  color: rexTechTheme.colors.primary.dark,
                  fontSize: rexTechTheme.typography.fontSize.regular,
                  fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm,
                  transition: 'all 0.2s ease',
                  ':hover': {
                    backgroundColor: rexTechTheme.colors.neutral.lightGray
                  },
                  ':disabled': {
                    opacity: 0.6,
                    cursor: 'not-allowed'
                  }
                }}
              >
                <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            
            </div>
          </div>
          
          {/* Date Filters */}
          <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.lg,
            boxShadow: rexTechTheme.shadows.light,
            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: rexTechTheme.spacing.md
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <span style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  fontWeight: rexTechTheme.typography.fontWeight.medium,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  Quick Filters:
                </span>
                <button
                  onClick={() => handlePeriodChange('day')}
                  style={{
                    padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.lg}`,
                    backgroundColor: periodFilter === 'day' ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.white,
                    border: `1px solid ${periodFilter === 'day' ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    cursor: 'pointer',
                    color: periodFilter === 'day' ? rexTechTheme.colors.neutral.white : rexTechTheme.colors.primary.dark,
                    fontSize: rexTechTheme.typography.fontSize.small,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: periodFilter === 'day' ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.lightGray
                    }
                  }}
                >
                  Today
                </button>
                <button
                  onClick={() => handlePeriodChange('week')}
                  style={{
                    padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.lg}`,
                    backgroundColor: periodFilter === 'week' ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.white,
                    border: `1px solid ${periodFilter === 'week' ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    cursor: 'pointer',
                    color: periodFilter === 'week' ? rexTechTheme.colors.neutral.white : rexTechTheme.colors.primary.dark,
                    fontSize: rexTechTheme.typography.fontSize.small,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: periodFilter === 'week' ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.lightGray
                    }
                  }}
                >
                  This Week
                </button>
                <button
                  onClick={() => handlePeriodChange('month')}
                  style={{
                    padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.lg}`,
                    backgroundColor: periodFilter === 'month' ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.white,
                    border: `1px solid ${periodFilter === 'month' ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    cursor: 'pointer',
                    color: periodFilter === 'month' ? rexTechTheme.colors.neutral.white : rexTechTheme.colors.primary.dark,
                    fontSize: rexTechTheme.typography.fontSize.small,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: periodFilter === 'month' ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.lightGray
                    }
                  }}
                >
                  This Month
                </button>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <span style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  fontWeight: rexTechTheme.typography.fontWeight.medium,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  Custom Range:
                </span>
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateRangeChange}
                  style={{
                    padding: rexTechTheme.spacing.xs,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.small,
                    fontSize: rexTechTheme.typography.fontSize.small
                  }}
                />
                <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>to</span>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateRangeChange}
                  style={{
                    padding: rexTechTheme.spacing.xs,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.small,
                    fontSize: rexTechTheme.typography.fontSize.small
                  }}
                />
                <button
                  onClick={handleDateRangeSubmit}
                  style={{
                    padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.lg}`,
                    backgroundColor: rexTechTheme.colors.primary.main,
                    border: 'none',
                    borderRadius: rexTechTheme.borderRadius.medium,
                    cursor: 'pointer',
                    color: rexTechTheme.colors.neutral.white,
                    fontSize: rexTechTheme.typography.fontSize.small,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    transition: 'all 0.2s ease',
                    ':hover': {
                      backgroundColor: rexTechTheme.colors.primary.dark
                    }
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: rexTechTheme.spacing.lg,
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <StatsCard
            title="Total Clients"
            value={stats.clients}
            icon={Users}
            color={rexTechTheme.colors.primary.main}
            trendDirection={trendData.clientDirection}
            trendValue={trendData.clientTrend}
            onClick={() => navigate('/admin/users?role=CLIENT')}
            loading={isRefreshing}
          />
          
          <StatsCard
            title="Collectors"
            value={stats.collectors}
            icon={UserCheck}
            color={rexTechTheme.colors.success}
            trendDirection={trendData.collectorDirection}
            trendValue={trendData.collectorTrend}
            onClick={() => navigate('/admin/users?role=COLLECTOR')}
            loading={isRefreshing}
          />
          
          <StatsCard
            title="Buyers"
            value={stats.buyers}
            icon={ShoppingBag}
            color={rexTechTheme.colors.info}
            trendDirection={trendData.buyerDirection}
            trendValue={trendData.buyerTrend}
            onClick={() => navigate('/admin/users?role=BUYER')}
            loading={isRefreshing}
          />
          
          <StatsCard
            title="Requests"
            value={stats.requests}
            icon={Package}
            color={rexTechTheme.colors.warning}
            trendDirection={trendData.requestDirection}
            trendValue={trendData.requestTrend}
            onClick={() => navigate('/admin/requests')}
            loading={isRefreshing}
          />
        </div>

        {/* Charts Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: rexTechTheme.spacing.xl,
          marginBottom: rexTechTheme.spacing.xl,
          '@media (max-width: 1024px)': {
            gridTemplateColumns: '1fr'
          }
        }}>
          {/* Client Growth Chart */}
          <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.xl,
            boxShadow: rexTechTheme.shadows.light,
            border: `1px solid ${rexTechTheme.colors.neutral.gray}`
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: rexTechTheme.spacing.lg
            }}>
              <div>
                <h3 style={{
                  color: rexTechTheme.colors.primary.dark,
                  fontSize: rexTechTheme.typography.fontSize.xlarge,
                  fontWeight: rexTechTheme.typography.fontWeight.bold,
                  margin: 0,
                  marginBottom: rexTechTheme.spacing.xs,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.sm
                }}>
                  <TrendingUp size={24} />
                  Client Growth
                </h3>
                <p style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  fontSize: rexTechTheme.typography.fontSize.small,
                  margin: 0
                }}>
                  {periodFilter === 'day' ? 'Daily' : periodFilter === 'week' ? 'Weekly' : 'Monthly'} client acquisition
                </p>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <span style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  Period:
                </span>
                <select
                  value={periodFilter}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  style={{
                    padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.sm}`,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.small,
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    color: rexTechTheme.colors.primary.dark,
                    fontSize: rexTechTheme.typography.fontSize.small
                  }}
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
            </div>
            
            <div style={{ height: '300px' }}>
              {clientGrowthData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clientGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={rexTechTheme.colors.neutral.gray} />
                    <XAxis 
                      dataKey="period" 
                      stroke={rexTechTheme.colors.neutral.darkGray}
                      fontSize={12}
                    />
                    <YAxis 
                      stroke={rexTechTheme.colors.neutral.darkGray}
                      fontSize={12}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="clients" 
                      name="New Clients"
                      stroke={rexTechTheme.colors.primary.main}
                      fill={rexTechTheme.colors.primary.lightest}
                      strokeWidth={3}
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: rexTechTheme.colors.neutral.darkGray
                }}>
                  <BarChart3 size={48} style={{ marginBottom: rexTechTheme.spacing.md, opacity: 0.5 }} />
                  <p>No client growth data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activities */}
          <div style={{
            backgroundColor: rexTechTheme.colors.neutral.white,
            borderRadius: rexTechTheme.borderRadius.large,
            padding: rexTechTheme.spacing.xl,
            boxShadow: rexTechTheme.shadows.light,
            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: rexTechTheme.spacing.lg
            }}>
              <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <Activity size={24} />
                Recent Activities
              </h3>
              <button
                onClick={() => navigate('/admin/activities')}
                style={{
                  padding: rexTechTheme.spacing.xs,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: rexTechTheme.colors.neutral.darkGray,
                  transition: 'all 0.2s ease',
                  ':hover': {
                    color: rexTechTheme.colors.primary.main
                  }
                }}
              >
                <MoreVertical size={20} />
              </button>
            </div>
            
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              maxHeight: '300px',
              paddingRight: rexTechTheme.spacing.xs
            }}>
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => (
                  <ActivityItem key={`${activity.id}-${index}`} activity={activity} />
                ))
              ) : (
                <div style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: rexTechTheme.colors.neutral.darkGray,
                  textAlign: 'center',
                  padding: rexTechTheme.spacing.xl
                }}>
                  <Activity size={48} style={{ marginBottom: rexTechTheme.spacing.md, opacity: 0.5 }} />
                  <p>No recent activities</p>
                </div>
              )}
            </div>
            
            <button
              onClick={() => navigate('/admin/activities')}
              style={{
                padding: rexTechTheme.spacing.md,
                backgroundColor: rexTechTheme.colors.primary.lightest,
                border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
                borderRadius: rexTechTheme.borderRadius.medium,
                color: rexTechTheme.colors.primary.main,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                cursor: 'pointer',
                marginTop: rexTechTheme.spacing.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: rexTechTheme.spacing.sm,
                transition: 'all 0.2s ease',
                ':hover': {
                  backgroundColor: rexTechTheme.colors.primary.lighter
                }
              }}
            >
              <Eye size={16} />
              View All Activities
            </button>
          </div>
        </div>

        {/* User Activity Chart */}
        <div style={{
          backgroundColor: rexTechTheme.colors.neutral.white,
          borderRadius: rexTechTheme.borderRadius.large,
          padding: rexTechTheme.spacing.xl,
          boxShadow: rexTechTheme.shadows.light,
          border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
          marginBottom: rexTechTheme.spacing.xl
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div>
              <h3 style={{
                color: rexTechTheme.colors.primary.dark,
                fontSize: rexTechTheme.typography.fontSize.xlarge,
                fontWeight: rexTechTheme.typography.fontWeight.bold,
                margin: 0,
                marginBottom: rexTechTheme.spacing.xs,
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm
              }}>
                <Users size={24} />
                User Activity
              </h3>
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                fontSize: rexTechTheme.typography.fontSize.small,
                margin: 0
              }}>
                {periodFilter === 'day' ? 'Daily' : periodFilter === 'week' ? 'Weekly' : 'Monthly'} user engagement
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.lg
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: rexTechTheme.spacing.xs }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: rexTechTheme.colors.primary.main,
                  borderRadius: '2px'
                }} />
                <span style={{
                  fontSize: rexTechTheme.typography.fontSize.small,
                  color: rexTechTheme.colors.neutral.darkGray
                }}>
                  Active Users
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: rexTechTheme.spacing.xs }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: rexTechTheme.colors.success,
                  borderRadius: '2px'
                }} />
                <span style={{
                  fontSize: rexTechTheme.typography.fontSize.small,
                  color: rexTechTheme.colors.neutral.darkGray
                }}>
                  New Users
                </span>
              </div>
            </div>
          </div>
          
          <div style={{ height: '300px' }}>
            {userActivityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={userActivityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={rexTechTheme.colors.neutral.gray} />
                  <XAxis 
                    dataKey="day" 
                    stroke={rexTechTheme.colors.neutral.darkGray}
                    fontSize={12}
                  />
                  <YAxis 
                    stroke={rexTechTheme.colors.neutral.darkGray}
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="active" 
                    name="Active Users"
                    stroke={rexTechTheme.colors.primary.main}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="new" 
                    name="New Users"
                    stroke={rexTechTheme.colors.success}
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: rexTechTheme.colors.neutral.darkGray
              }}>
                <BarChart3 size={48} style={{ marginBottom: rexTechTheme.spacing.md, opacity: 0.5 }} />
                <p>No user activity data available</p>
              </div>
            )}
          </div>
        </div>

        
   
          
         
        
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;